import React, {
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import { useTranslation } from 'react-i18next';

// leaflet
import L from 'leaflet';
import {
  Map, Marker, Popup, Tooltip, LayerGroup, LayersControl, TileLayer,
} from 'react-leaflet';
import {
  orderBy,
} from 'lodash';

import FullscreenControl from 'react-leaflet-fullscreen';
import { GestureHandling } from 'leaflet-gesture-handling';

import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Table from '@material-ui/core/Table';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableRow from '@material-ui/core/TableRow';
import TableBody from '@material-ui/core/TableBody';
import TableHead from '@material-ui/core/TableHead';
import Button from '@material-ui/core/Button';
import withWidth from '@material-ui/core/withWidth';
import LeafletFullScreenFix from '../../../utils/map/LeafletFullScreenFix';

// icon
import { FaChartLine } from '../../../assets/font/Fontawesome';
import { RainIcon } from '../../../assets/svg/Icon';

// components
import mapConfig from '../../../components/map/Config';
import LayerRiver from '../../../components/map/River';
import LayerProvince from '../../../components/map/Province';
import LayerBasin from '../../../components/map/Basin';
import WmsBoundary from '../../../components/map/WmsBoundary';
import WmsBasin from '../../../components/map/WmsBasin';

import RainfallStatusColor from './RinfallStatusColor';
import RainfallChart from '../chart/RainfallChart';
import RainfallModal from '../../../components/modal/Modal';

// utils
import formatNumber from '../../../utils/number/Number';

// style
import 'leaflet/dist/leaflet.css';
import 'react-leaflet-fullscreen/dist/styles.css';
import 'leaflet-gesture-handling/dist/leaflet-gesture-handling.css';
import styles from './style';

const useStyles = makeStyles(styles);

const { Overlay } = LayersControl;

function RainfallMap(props) {
  const { t } = useTranslation();
  const classes = useStyles();
  const refMap = useRef(null);
  const refRainfall24hLayer = useRef(null);
  const refRainTodayLayer = useRef(null);
  const refRainYesterdayLayer = useRef(null);
  const refRainMonthLayer = useRef(null);
  const refRainYearLayer = useRef(null);
  const refRain3dayLayer = useRef(null);
  const refRain7dayLayer = useRef(null);
  const {
    dataRainfall24, datasRainToday,
    datasRainYesterday, handleSetMap,
    datasRainMonth, datasRainYear,
    datasRain3day,
    datasRain7day, type,
    currentMode, currentArea,
  } = props;
  const [modalData, setModalData] = useState({
    open: false,
    header: t('กราฟ'),
    subheader: null,
    content: null,
  });

  const bindMarker = (id) => (ref) => {
    const { markers } = props;

    if (ref) {
      markers[id] = ref.leafletElement;
    }
  };

  const handleChart = (data) => (e) => {
    e.preventDefault();

    setModalData({
      open: true,
      header: t('กราฟ'),
      subheader: null,
      content: <RainfallChart
        filterData={dataRainfall24}
        id={data.station.id}
        provinceId={data.geocode.province_code}
        selectedType={type}
        year={moment(data.rainfall_datetime).format('YYYY')}
        month={moment(data.rainfall_datetime).format('MM')}
        classes={classes}
      />,
    });
  };

  const handleChartYesterday = (data) => (e) => {
    e.preventDefault();

    setModalData({
      open: true,
      header: t('กราฟ'),
      subheader: null,
      content: <RainfallChart
        filterData={datasRainYesterday}
        id={data.tele_station_id}
        provinceId={data.province_code}
        selectedType={type}
        year={(data.rainfall_datetime).substring(0, 4)}
        month={(data.rainfall_datetime).substring(5, 7)}
        classes={classes}
      />,
    });
  };

  function setIcon(val, trans) {
    const status = RainfallStatusColor(val, trans);

    return status;
  }

  function PopupRainfall(data) {
    let value = null;
    if (type === 'rainfall24h') value = parseFloat(data.rain_24h).toFixed(2);
    if (type === 'rainfallToday' || type === 'rainfallYesterday' || type === 'rainfallMonth' || type === 'rainfallYear') value = data.rainfall_value;
    if (type === 'rainfall3day') value = parseFloat(data.rain_3d).toFixed(2);
    if (type === 'rainfall7day') value = parseFloat(data.rain_7d).toFixed(2);

    const rainfallStatus = setIcon(parseFloat(value), type).color.statusColor;
    const textRainfallStatus = setIcon(parseFloat(value), type).color.constrastColor;

    return (
      <React.Fragment key="key">
        <TableContainer>
          <Table size="small">
            <TableHead />
            <TableBody>
              <TableRow>
                <TableCell className={classes.hiddenLine} padding="none" align="center" colSpan={3}>
                  <Box fontWeight="500" fontFamily="Kanit" fontSize="14px">
                    {`${data.station.tele_station_name.th} `}
                  </Box>
                  <Box fontFamily="Kanit" fontSize="12px">
                    {`${data.geocode.amphoe_name.th} `}
                    {`${data.geocode.tumbon_name.th} `}
                  </Box>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.hiddenLine} padding="none" align="center" colSpan={3}>
                  <Box py={3} display="flex" justifyContent="center" alignItems="center">
                    <RainIcon
                      style={{
                        fontSize: '4rem',
                        fill: rainfallStatus,
                        stroke: rainfallStatus === '#ffffff' ? '#000' : undefined,
                      }}
                    />
                    <Box>
                      <Box
                        p={1}
                        className={classes.statusText}
                        fontFamily="Kanit"
                        bgcolor={rainfallStatus}
                        color={textRainfallStatus}
                        border={rainfallStatus === '#ffffff' ? 1 : undefined}
                      >
                        {formatNumber(value)}
                      </Box>
                      <Box fontFamily="Kanit" fontSize={12}>
                        (
                        {t('มม.')}
                        )
                      </Box>
                    </Box>
                  </Box>
                  <Box mb={2} fontSize="16px" fontFamily="Kanit">
                    {t('ปริมาณฝน')}
                  </Box>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell padding="none" align="center" colSpan={3} />
              </TableRow>
              <TableRow>
                <TableCell className={classes.hiddenLine} colSpan={2}>
                  <Box color="text.disabled" fontSize="8px">
                    {`${t('ข้อมูล')}: ${data.agency.agency_name.th}`}
                  </Box>
                  <Box color="text.disabled" fontSize="8px">
                    {((type === 'rainfall24h') || (type === 'rainfallToday')) && (
                      <>
                        {`${t('ล่าสุด')} : ${data.rainfall_datetime} น.`}
                      </>
                    )}
                    {((type === 'rainfall3day') || (type === 'rainfall7day')) && (
                      <>
                        {`${t('ล่าสุด')} : ${data.rainfall_end_date}`}
                      </>
                    )}
                  </Box>
                </TableCell>
                <TableCell className={classes.hiddenLine} align="right">
                  {type === 'rainfall24h' && (
                    data.agency.agency_shortname.en !== 'DWR' ? (
                      <Button
                        onClick={handleChart(data, type)}
                        title={`สถานี ${data.station.tele_station_name.th} \n${t('คลิ๊กเพื่อแสดงกราฟฝน')}`}
                        style={{ padding: 0, minWidth: 18 }}
                      >
                        <FaChartLine className={classes.rightIcon} />
                      </Button>
                    ) : (
                      <Button
                        title={`สถานี ${data.station.tele_station_name.th} \n ${t('ไม่มีข้อมูลฝนรายชั่วโมง')}`}
                        style={{ padding: 0, minWidth: 18 }}
                      >
                        <FaChartLine className={classes.rightIcon} style={{ padding: 0, color: '#dddddd' }} />
                      </Button>
                    )
                  )}
                  {type !== 'rainfall24h' && (
                    <Button
                      onClick={handleChart(data, type)}
                      title={`สถานี ${data.station.tele_station_name.th} \n${t('คลิ๊กเพื่อแสดงกราฟฝน')}`}
                      style={{ padding: 0, minWidth: 18 }}
                    >
                      <FaChartLine className={classes.rightIcon} />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </React.Fragment>
    );
  }

  function eachRainfallStation(data, i) {
    if (data.station.tele_station_lat && data.station.tele_station_long) {
      // marker position
      const position = [parseFloat(data.station.tele_station_lat),
        parseFloat(data.station.tele_station_long)];
      // marker icons
      let value = null;
      let teleStationId = data.tele_station_id;
      if (type === 'rainfall24h') value = data.rain_24h;
      if (type === 'rainfallToday' || type === 'rainfallMonth' || type === 'rainfallYear') value = data.rainfall_value;
      if (type === 'rainfall3day') value = data.rain_3d; teleStationId = data.station.id;
      if (type === 'rainfall7day') value = data.rain_7d; teleStationId = data.station.id;

      if (value !== null) {
        const icon = setIcon(parseFloat(value), type);
        return (
          <Marker
            id={teleStationId}
            key={i}
            zIndexOffset={i}
            position={position}
            icon={icon.icon}
            ref={bindMarker(teleStationId)}
          >
            <Popup autoPan>
              {PopupRainfall(data)}
            </Popup>
            <Tooltip>
              <span>
                <strong>
                  {t('สถานี')}
                  {data.station.tele_station_name.th}
                </strong>
                <br />
                {`${t('ต.')}${data.geocode.tumbon_name.th} ${t('อ.')}${data.geocode.amphoe_name.th} `}
                {`${t('จ.')} ${data.geocode.province_name.th}`}
                <br />
                {`${t('ปริมาณฝน')}`}
                {' '}
                <strong>
                  {value}
                </strong>
                {' '}
                {`${t('ม.ม.')}`}
                <br />
                {((type === 'rainfall24h') || (type === 'rainfallToday')) && (
                  <>
                    {`${t('ล่าสุด')} : ${data.rainfall_datetime} น.`}
                  </>
                )}
                {((type === 'rainfall3day') || (type === 'rainfall7day')) && (
                  <>
                    {`${t('ล่าสุด')} : ${data.rainfall_end_date}`}
                  </>
                )}
                <br />
                {`${t('ข้อมูล')}: ${data.agency.agency_name.th} `}
              </span>
            </Tooltip>
          </Marker>
        );
      }
    }
    return false;
  }

  function PopupRainfallYesterday(data) {
    const value = data.rainfall_value;

    const rainfallStatus = setIcon(parseFloat(value), type).color.statusColor;
    const textRainfallStatus = setIcon(parseFloat(value), type).color.constrastColor;

    return (
      <React.Fragment key="key">
        <TableContainer>
          <Table size="small">
            <TableHead />
            <TableBody>
              <TableRow>
                <TableCell className={classes.hiddenLine} padding="none" align="center" colSpan={3}>
                  <Box fontWeight="500" fontFamily="Kanit" fontSize="14px">
                    {`${data.tele_station_name.th} `}
                  </Box>
                  <Box fontFamily="Kanit" fontSize="12px">
                    {`${data.amphoe_name.th} `}
                    {`${data.tumbon_name.th} `}
                  </Box>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.hiddenLine} padding="none" align="center" colSpan={3}>
                  <Box py={3} display="flex" justifyContent="center" alignItems="center">
                    <RainIcon style={{ fontSize: '4rem', fill: rainfallStatus }} />
                    <Box>
                      <Box p={1} className={classes.statusText} fontFamily="Kanit" bgcolor={rainfallStatus} color={textRainfallStatus}>{value}</Box>
                      <Box fontFamily="Kanit" fontSize={12}>
                        (
                        {t('มม.')}
                        )
                      </Box>
                    </Box>
                  </Box>
                  <Box mb={2} fontSize="16px" fontFamily="Kanit">
                    {t('ปริมาณฝน')}
                  </Box>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell padding="none" align="center" colSpan={3} />
              </TableRow>
              <TableRow>
                <TableCell className={classes.hiddenLine} colSpan={2}>
                  <Box color="text.disabled" fontSize="8px">
                    {`${t('ข้อมูล')}: ${data.agency_name.th}`}
                  </Box>
                  <Box color="text.disabled" fontSize="8px">
                    {`${t('ล่าสุด')} : ${data.rainfall_datetime} น.`}
                  </Box>
                </TableCell>
                <TableCell className={classes.hiddenLine} align="right">
                  <Button
                    onClick={handleChartYesterday(data, type)}
                    title={`สถานี ${data.tele_station_name.th} \n${t('คลิ๊กเพื่อแสดงกราฟฝน')}`}
                    style={{ padding: 0, minWidth: 18 }}
                  >
                    <FaChartLine className={classes.rightIcon} />
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </React.Fragment>
    );
  }

  function eachRainfallStationYesterday(data, i) {
    if (data.tele_station_lat && data.tele_station_long && data.tele_station_name.th) {
      // marker position
      const position = [parseFloat(data.tele_station_lat),
        parseFloat(data.tele_station_long)];
      // marker icons
      const value = data.rainfall_value;

      const icon = setIcon(parseFloat(value), type);
      const currentDatetime = data.rainfall_datetime;
      let splitDatetime = [];
      splitDatetime = currentDatetime.split(' ');

      return (
        <Marker
          id={data.tele_station_id}
          key={i}
          position={position}
          icon={icon.icon}
          zIndexOffset={i}
          ref={bindMarker(data.tele_station_id)}
        >
          <Popup autoPan>
            {PopupRainfallYesterday(data)}
          </Popup>
          <Tooltip>
            <span>
              <strong>
                {t('สถานี')}
                {data.tele_station_name.th}
              </strong>
              <br />
              {`${t('ต.')}${data.tumbon_name.th} ${t('อ.')}${data.amphoe_name.th} `}
              {`${t('จ.')} ${data.province_name.th}`}
              <br />
              {`${t('ปริมาณฝน')}`}
              {' '}
              <strong>
                {value}
              </strong>
              {' '}
              {`${t('ม.ม.')}`}
              <br />
              {`${t('ล่าสุด')} : ${splitDatetime[0]}`}
              <br />
              {`${t('ข้อมูล')}: ${data.agency_name.th} `}
            </span>
          </Tooltip>
        </Marker>
      );
    }
    return false;
  }

  useEffect(() => {
    handleSetMap(refMap.current.leafletElement);

    setModalData({
      open: false,
      header: '',
      subheader: null,
      content: null,
    });

    // set bounds
    if (currentArea.top_left && currentArea.bottom_right) {
      refMap.current.leafletElement.fitBounds(
        L.latLngBounds(currentArea.top_left, currentArea.bottom_right), { padding: [50, 50] },
      );
    }
    L.Map.addInitHook('addHandler', 'gestureHandling', GestureHandling);
  }, [handleSetMap, currentMode, currentArea]);

  const Modal = React.memo((p) => (
    <RainfallModal
      open={p.modalData.open}
      modalHeader={p.modalData.header}
      modalSubHeader={p.modalData.subheader}
      modalContent={p.modalData.content}
    />
  ));

  return (
    <div className={classes.root}>
      <Map
        ref={refMap}
        center={currentArea.location ? currentArea.location : mapConfig.center}
        zoomSnap={0}
        zoomDelta={0.5}
        style={{ height: 633, zIndex: 0 }}
        zoom={currentArea.id !== 0 ? currentArea.zoom - 1 : mapConfig.zoom}
        boundsOptions={{ padding: [50, 50] }}
        gestureHandling
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          id="OpenStreetMap"
          attribution="&copy; <a href=&quot;http://osm.org/copyright&quot;>ESRI World Street Map</a> contributors"
        />
        {currentMode === 'province' && (
          <LayerProvince
            areaType={0}
            areaId={currentArea.code}
          />
        )}
        {currentMode === 'basin' && (
          <LayerBasin
            areaType={1}
            areaId={currentArea.code}
          />
        )}
        <LayersControl position="topright">
          {(type === 'rainfall24h') && (
            <Overlay name={t('ฝนสะสม 24 ชม. ย้อนหลัง')} checked="true">
              <LayerGroup ref={refRainfall24hLayer}>
                {/* {dataRainfall24.map(eachRainfallStation)} */}
                {orderBy(dataRainfall24, ['rain_24h'], ['asc']).map(eachRainfallStation)}
              </LayerGroup>
            </Overlay>
          )}
          {(type === 'rainfallToday') && (
            <Overlay name={t('ฝนวันนี้')} checked="true">
              <LayerGroup ref={refRainTodayLayer}>
                {/* {datasRainToday.map(eachRainfallStation)} */}
                {orderBy(datasRainToday, ['rainfall_value'], ['asc']).map(eachRainfallStation)}
              </LayerGroup>
            </Overlay>
          )}
          {(type === 'rainfallYesterday') && (
            <Overlay name={t('ฝนวานนี้')} checked="true">
              <LayerGroup ref={refRainYesterdayLayer}>
                {/* {datasRainYesterday.map(eachRainfallStationYesterday)} */}
                {orderBy(datasRainYesterday, ['rainfall_value'], ['asc']).map(eachRainfallStationYesterday)}
              </LayerGroup>
            </Overlay>
          )}
          {(type === 'rainfall3day') && (
            <Overlay name={t('ฝน 3 วัน')} checked="true">
              <LayerGroup ref={refRain3dayLayer}>
                {/* {datasRain3day.map(eachRainfallStation)} */}
                {orderBy(datasRain3day, ['rain_3d'], ['asc']).map(eachRainfallStation)}
              </LayerGroup>
            </Overlay>
          )}
          {(type === 'rainfall7day') && (
            <Overlay name={t('ฝน 7 วัน')} checked="true">
              <LayerGroup ref={refRain7dayLayer}>
                {/* {datasRain7day.map(eachRainfallStation)} */}
                {orderBy(datasRain7day, ['rain_7d'], ['asc']).map(eachRainfallStation)}
              </LayerGroup>
            </Overlay>
          )}
          {(type === 'rainfallMonth') && (
            <Overlay name={t('ฝนรายเดือน')} checked="true">
              <LayerGroup ref={refRainMonthLayer}>
                {/* {datasRainMonth.map(eachRainfallStation)} */}
                {orderBy(datasRainMonth, ['rainfall_value'], ['asc']).map(eachRainfallStation)}
              </LayerGroup>
            </Overlay>
          )}
          {(type === 'rainfallYear') && (
            <Overlay name={t('ฝนรายปี')} checked="true">
              <LayerGroup ref={refRainYearLayer}>
                {datasRainYear.map(eachRainfallStation)}
                {orderBy(datasRainYear, ['rainfall_value'], ['asc']).map(eachRainfallStation)}
              </LayerGroup>
            </Overlay>
          )}
          <Overlay name={t('เส้นแม่น้ำ')} checked>
            <LayerRiver />
          </Overlay>
          <Overlay name={t('ขอบเขตการปกครอง')}>
            <WmsBoundary />
          </Overlay>
          <Overlay name={t('ขอบเขตลุ่มน้ำ')}>
            <WmsBasin />
          </Overlay>
        </LayersControl>
        <FullscreenControl
          position="topleft"
          forcePseudoFullscreen
          content={LeafletFullScreenFix()}
        />
      </Map>

      <Modal modalData={modalData} />
    </div>
  );
}

RainfallMap.propTypes = {
  dataRainfall24: PropTypes.instanceOf(Array).isRequired,
  datasRainToday: PropTypes.instanceOf(Array).isRequired,
  datasRainYesterday: PropTypes.instanceOf(Array).isRequired,
  datasRainMonth: PropTypes.instanceOf(Array).isRequired,
  datasRainYear: PropTypes.instanceOf(Array).isRequired,
  datasRain3day: PropTypes.instanceOf(Array).isRequired,
  datasRain7day: PropTypes.instanceOf(Array).isRequired,
  handleSetMap: PropTypes.func.isRequired,
  markers: PropTypes.instanceOf(Array).isRequired,
  type: PropTypes.string.isRequired,
  currentMode: PropTypes.string.isRequired,
  currentArea: PropTypes.instanceOf(Object).isRequired,
};

export default withWidth()(RainfallMap);
